import React, { useEffect, useMemo, useRef, useState } from "react";
import "../Style/SingleDatePicker.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDate(value) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || "");
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function formatDate(date) {
  return [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    date.getFullYear(),
  ].join("/");
}

function formatTypedDate(value) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function sameDay(first, second) {
  return (
    first &&
    second &&
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}

function getCalendarDays(month) {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - firstDay.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
}

export default function SingleDatePicker({
  value = "",
  onChange,
  placeholder = "DD/MM/YYYY",
  disabled = false,
  className = "",
  minDate,
  maxDate,
}) {
  const selectedDate = useMemo(() => parseDate(value), [value]);
  const minimum = useMemo(
    () => (minDate ? startOfDay(minDate) : null),
    [minDate],
  );
  const maximum = useMemo(
    () => (maxDate ? startOfDay(maxDate) : null),
    [maxDate],
  );
  const initialMonth = selectedDate || minimum || new Date();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [displayMonth, setDisplayMonth] = useState(
    () => new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1),
  );
  const wrapperRef = useRef(null);

  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (!selectedDate) return;
    setDisplayMonth(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
    );
  }, [selectedDate]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const days = useMemo(() => getCalendarDays(displayMonth), [displayMonth]);
  const today = useMemo(() => startOfDay(new Date()), []);
  const currentYear = new Date().getFullYear();
  const firstYear = minimum?.getFullYear() ?? currentYear - 5;
  const lastYear = maximum?.getFullYear() ?? currentYear + 10;
  const years = Array.from(
    { length: lastYear - firstYear + 1 },
    (_, index) => firstYear + index,
  );

  const isUnavailable = (date) =>
    (minimum && date < minimum) || (maximum && date > maximum);

  const chooseDate = (date) => {
    if (isUnavailable(date)) return;
    const formatted = formatDate(date);
    setDraft(formatted);
    setOpen(false);
    onChange?.(formatted);
    setDisplayMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  const commitDraft = () => {
    if (!draft) {
      onChange?.("");
      return;
    }

    const parsed = parseDate(draft);
    if (!parsed || isUnavailable(parsed)) {
      setDraft(value);
      return;
    }

    const formatted = formatDate(parsed);
    setDraft(formatted);
    onChange?.(formatted);
  };

  const shiftMonth = (offset) => {
    setDisplayMonth(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + offset, 1),
    );
  };

  const previousMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() - 1,
    1,
  );
  const nextMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() + 1,
    1,
  );
  const previousDisabled =
    minimum &&
    previousMonth <
      new Date(minimum.getFullYear(), minimum.getMonth(), 1);
  const nextDisabled =
    maximum &&
    nextMonth > new Date(maximum.getFullYear(), maximum.getMonth(), 1);

  return (
    <div
      className={`single-date-picker ${className}`.trim()}
      ref={wrapperRef}
    >
      <div
        className={`single-date-field${open ? " is-open" : ""}${
          disabled ? " is-disabled" : ""
        }`}
      >
        <input
          type="text"
          inputMode="numeric"
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Preferred date"
          onChange={(event) => {
            const nextValue = formatTypedDate(event.target.value);
            setDraft(nextValue);
            if (!nextValue) onChange?.("");
          }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
              setOpen(false);
            }
          }}
        />
        <button
          type="button"
          disabled={disabled}
          aria-label={open ? "Close calendar" : "Open calendar"}
          onClick={() => !disabled && setOpen((current) => !current)}
        >
          <span className="single-date-calendar-icon" aria-hidden="true" />
        </button>
      </div>

      {open && !disabled && (
        <div className="single-date-popover">
          <div className="single-date-topline">
            <span>Choose a date</span>
            <span aria-hidden="true">&#10022;</span>
          </div>

          <div className="single-date-navigation">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              disabled={previousDisabled}
              aria-label="Previous month"
            >
              &lsaquo;
            </button>

            <div className="single-date-caption">
              <select
                aria-label="Month"
                value={displayMonth.getMonth()}
                onChange={(event) =>
                  setDisplayMonth(
                    new Date(
                      displayMonth.getFullYear(),
                      Number(event.target.value),
                      1,
                    ),
                  )
                }
              >
                {MONTHS.map((month, index) => (
                  <option value={index} key={month}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                aria-label="Year"
                value={displayMonth.getFullYear()}
                onChange={(event) =>
                  setDisplayMonth(
                    new Date(
                      Number(event.target.value),
                      displayMonth.getMonth(),
                      1,
                    ),
                  )
                }
              >
                {years.map((year) => (
                  <option value={year} key={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => shiftMonth(1)}
              disabled={nextDisabled}
              aria-label="Next month"
            >
              &rsaquo;
            </button>
          </div>

          <div className="single-date-weekdays" aria-hidden="true">
            {WEEKDAYS.map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>

          <div className="single-date-grid" role="grid">
            {days.map((date) => {
              const outside = date.getMonth() !== displayMonth.getMonth();
              const unavailable = isUnavailable(date);
              const selected = sameDay(date, selectedDate);
              const isToday = sameDay(date, today);

              return (
                <button
                  type="button"
                  role="gridcell"
                  key={date.toISOString()}
                  disabled={unavailable}
                  className={[
                    outside ? "is-outside" : "",
                    selected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={date.toLocaleDateString(undefined, {
                    dateStyle: "full",
                  })}
                  aria-selected={selected}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => chooseDate(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="single-date-footer">
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setDraft("");
                onChange?.("");
                setOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              disabled={isUnavailable(today)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => chooseDate(today)}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
